import {
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  ExpandMore,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as ViewIcon,
  Sell as SellIcon
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Asset,
  clearAssets,
  getAllAssets,
  GetAllAssetsPayload
} from 'slices/fundManagementInvestment/fundmanagementInvestmentSlice';
import RevaluationModal from './RevaluationModal';

// Tab Panel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`asset-tabpanel-${index}`}
      aria-labelledby={`asset-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: { xs: 0.5, sm: 1, md: 1.5 } }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `asset-tab-${index}`,
    'aria-controls': `asset-tabpanel-${index}`
  };
}

interface RevaluationModalState {
  open: boolean;
  asset: Asset | null;
}

interface AssetDetailModalState {
  open: boolean;
  asset: Asset | null;
}

type UnitType = 'incomeUnits' | 'growthUnits';

interface Pagination {
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  hasMore: boolean;
  nextToken: string | null;
}

const AssetList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Redux selectors with proper typing
  const assets = useAppSelector((state: any) => state.fundManagementInvestment.assets as Asset[]);
  const loading = useAppSelector((state: any) => state.fundManagementInvestment.loading as boolean);
  const error = useAppSelector(
    (state: any) => state.fundManagementInvestment.error as string | null
  );
  const pagination = useAppSelector(
    (state: any) => state.fundManagementInvestment.pagination as Pagination
  );

  // Get groupId from Redux state
  const selectedProjectId = useAppSelector(
    (state: any) => state.homeTilesServices.selectedProjectId || ''
  );

  // Tab state
  const [tabValue, setTabValue] = useState<number>(0);
  const [currentUnitType, setCurrentUnitType] = useState<UnitType>('incomeUnits');

  // Modal states
  const [revaluationModal, setRevaluationModal] = useState<RevaluationModalState>({
    open: false,
    asset: null
  });

  const [assetDetailModal, setAssetDetailModal] = useState<AssetDetailModalState>({
    open: false,
    asset: null
  });

  // ✅ SMOOTH SCROLL TO TOP ON COMPONENT MOUNT
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number): void => {
    setTabValue(newValue);
    const unitType: UnitType = newValue === 0 ? 'incomeUnits' : 'growthUnits';
    setCurrentUnitType(unitType);

    // Clear existing assets before fetching new ones
    dispatch(clearAssets());

    // Fetch assets for the new unit type
    fetchAssets(null, unitType);

    // Smooth scroll to top when switching tabs
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Fetch assets using Redux thunk
  const fetchAssets = async (
    continuationToken: string | null = null,
    unitType: UnitType = currentUnitType
  ): Promise<void> => {
    const payload: GetAllAssetsPayload = {
      groupId: selectedProjectId,
      serviceId: 'fundManagementInvestment',
      unitType,
      pageSize: 25,
      continuationToken
    };

    try {
      await dispatch(getAllAssets(payload)).unwrap();
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  // Load assets on component mount
  useEffect(() => {
    if (selectedProjectId) {
      fetchAssets();
    }
  }, [selectedProjectId]);

  // Handle modals
  const openRevaluationModal = (asset: Asset): void => {
    setRevaluationModal({
      open: true,
      asset
    });
  };

  const closeRevaluationModal = (): void => {
    setRevaluationModal({
      open: false,
      asset: null
    });
  };

  const openAssetDetailModal = (asset: Asset): void => {
    setAssetDetailModal({
      open: true,
      asset
    });
  };

  const closeAssetDetailModal = (): void => {
    setAssetDetailModal({
      open: false,
      asset: null
    });
  };

  // ✅ NEW: Handle Sell Asset - Navigate to SellAssets page with prefilled data
  const handleSellAsset = (asset: Asset): void => {
    navigate('/fundManagementInvestment/sell-assets', {
      state: {
        prefilledAsset: {
          assetId: asset.assetId,
          assetName: asset.assetData.identifier,
          assetType: asset.type,
          currentValue: parseFloat(asset.currentValue || asset.assetData.currentValue || '0'),
          purchaseValue: parseFloat(asset.assetData.purchaseValue || '0'),
          purchaseDate: asset.purchaseDate,
          subType: asset.assetData.subType
        },
        unitType: currentUnitType,
        fromAssetList: true
      }
    });
  };

  // Handle successful revaluation
  const handleRevaluationSuccess = (): void => {
    // Refresh the assets list
    fetchAssets();
  };

  // Load more assets
  const handleLoadMore = (): void => {
    if (pagination?.hasMore && pagination?.nextToken) {
      fetchAssets(pagination.nextToken);
    }
  };

  // Format currency
  const formatCurrency = (amount: string | number): string => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(Number(amount) || 0);
  };

  // Enhanced date formatting function
  const formatDateTime = (dateString: string | undefined): string => {
    if (!dateString) return 'Not provided';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return date.toLocaleDateString('en-AU', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Australia/Sydney'
      });
    } catch {
      return dateString;
    }
  };

  // Format simple dates (YYYY-MM-DD)
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'Not provided';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return date.toLocaleDateString('en-AU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Format percentage values
  const formatPercentage = (value: string): string => {
    if (value.includes('%')) return value;
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    return `${numValue}%`;
  };

  // Clean up field names for better display
  const formatFieldName = (key: string): string => {
    const fieldNameMappings: { [key: string]: string } = {
      assetId: 'Asset ID',
      groupId: 'Group ID',
      serviceId: 'Service ID',
      unitType: 'Unit Type',
      subType: 'Sub Type',
      purchaseValue: 'Purchase Value',
      expectedAnnualYield: 'Expected Annual Yield',
      cashUsed: 'Cash Used',
      netMonthlyIncome: 'Net Monthly Income',
      totalGrossIncome: 'Total Gross Income',
      totalExpenses: 'Total Expenses',
      createdAt: 'Created Date',
      purchaseDate: 'Purchase Date',
      fundManager: 'Fund Manager',
      currentValue: 'Current Value',
      maturityDate: 'Maturity Date',
      leaseExpiry: 'Lease Expiry Date',
      dividendFrequency: 'Dividend Frequency',
      paymentFrequency: 'Payment Frequency',
      holdingPeriod: 'Holding Period',
      exitStrategy: 'Exit Strategy',
      developmentTimeline: 'Development Timeline',
      additionalNotes: 'Additional Notes',
      developmentPotential: 'Development Potential',
      completionTarget: 'Completion Target',
      growthDrivers: 'Growth Drivers',
      dividendPolicy: 'Dividend Policy',
      lockupPeriod: 'Lockup Period',
      managementFee: 'Management Fee',
      carriedInterest: 'Carried Interest',
      couponRate: 'Coupon Rate',
      monthlyIncome: 'Monthly Income',
      monthlyExpenses: 'Monthly Expenses',
      additionalInfo: 'Additional Information',
      assetData: 'Asset Information',
      liabilities: 'Liabilities',
      primary: 'Primary Income',
      secondary: 'Secondary Income',
      other: 'Other Income',
      financing: 'Financing Costs',
      management: 'Management Fees',
      operational: 'Operational Costs',
      fees: 'Other Fees',
      type: 'Liability Type',
      provider: 'Provider',
      amount: 'Amount',
      rate: 'Interest Rate',
      monthlyPayment: 'Monthly Payment',
      identifier: 'Asset Identifier',
      description: 'Description',
      quantity: 'Quantity',
      exchange: 'Exchange',
      sector: 'Sector'
    };

    return (
      fieldNameMappings[key] ||
      key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())
    );
  };

  // Enhanced value formatting with better date detection
  const formatAssetValue = (key: string, value: any): string => {
    if (value === null) return 'Not provided';
    if (value === undefined) return 'Not provided';
    if (value === '') return 'Not provided';

    // Handle arrays
    if (Array.isArray(value)) {
      return value.length === 0 ? 'None' : JSON.stringify(value, null, 2);
    }

    // Handle dates - improved detection
    if (typeof value === 'string') {
      // ISO datetime format (with T and timezone)
      if (value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        return formatDateTime(value);
      }
      // Simple date format (YYYY-MM-DD)
      else if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return formatDate(value);
      }

      // Handle currency fields
      if (
        key.toLowerCase().includes('value') ||
        key.toLowerCase().includes('price') ||
        key.toLowerCase().includes('income') ||
        key.toLowerCase().includes('expense') ||
        key.toLowerCase().includes('cash') ||
        key.toLowerCase().includes('fee') ||
        key.toLowerCase().includes('payment') ||
        key === 'primary' ||
        key === 'secondary' ||
        key === 'other' ||
        key === 'financing' ||
        key === 'management' ||
        key === 'operational' ||
        key === 'fees' ||
        key === 'amount' ||
        key === 'monthlyPayment'
      ) {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          return formatCurrency(numValue);
        }
      }

      // Handle percentage fields
      if (
        key.toLowerCase().includes('yield') ||
        key.toLowerCase().includes('rate') ||
        key.toLowerCase().includes('interest') ||
        key === 'rate'
      ) {
        return formatPercentage(value);
      }
    }

    return String(value);
  };

  // Function to render any object as key-value pairs recursively
  const renderAssetKeyValuePairs = (obj: any, level = 0): JSX.Element[] => {
    if (!obj || typeof obj !== 'object') {
      return [];
    }

    const items: JSX.Element[] = [];

    Object.entries(obj).forEach(([key, value]) => {
      const fieldName = formatFieldName(key);

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        // Nested object - render as accordion (EXPANDED BY DEFAULT)
        items.push(
          <TableRow key={`${level}-${key}`}>
            <TableCell colSpan={2} sx={{ p: 0 }}>
              <Accordion
                elevation={0}
                sx={{
                  boxShadow: 'none',
                  border: 'none',
                  '&:before': {
                    display: 'none'
                  }
                }}
                defaultExpanded={true}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    backgroundColor: level === 0 ? theme.palette.grey[50] : theme.palette.grey[100],
                    minHeight: { xs: 32, md: 36 },
                    '& .MuiAccordionSummary-content': { margin: '6px 0' },
                    borderRadius: 1,
                    px: { xs: 1, md: 1.5 }
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: '0.75rem', md: '0.8rem' },
                      color: theme.palette.text.primary
                    }}
                  >
                    {fieldName}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Table size="small">
                    <TableBody>{renderAssetKeyValuePairs(value, level + 1)}</TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            </TableCell>
          </TableRow>
        );
      } else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
        // Array of objects (like liabilities)
        items.push(
          <TableRow key={`${level}-${key}`}>
            <TableCell colSpan={2} sx={{ p: 0 }}>
              <Accordion
                elevation={0}
                sx={{
                  boxShadow: 'none',
                  border: 'none',
                  '&:before': {
                    display: 'none'
                  }
                }}
                defaultExpanded={true}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    backgroundColor: level === 0 ? theme.palette.grey[50] : theme.palette.grey[100],
                    minHeight: { xs: 32, md: 36 },
                    '& .MuiAccordionSummary-content': { margin: '6px 0' },
                    borderRadius: 1,
                    px: { xs: 1, md: 1.5 }
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: '0.75rem', md: '0.8rem' },
                      color: theme.palette.text.primary
                    }}
                  >
                    {fieldName} ({value.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {value.map((item: any, index: number) => (
                    <Box key={index} sx={{ mb: index < value.length - 1 ? 1 : 0 }}>
                      {index > 0 && <Divider sx={{ my: 0.5 }} />}
                      <Table size="small">
                        <TableBody>{renderAssetKeyValuePairs(item, level + 1)}</TableBody>
                      </Table>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            </TableCell>
          </TableRow>
        );
      } else {
        // Simple value
        const displayValue = formatAssetValue(key, value);

        items.push(
          <TableRow
            key={`${level}-${key}`}
            sx={{
              '&:nth-of-type(odd)': {
                backgroundColor: level === 0 ? theme.palette.grey[50] : '#ffffff'
              },
              '&:hover': { backgroundColor: theme.palette.action.hover },
              transition: 'background-color 0.2s'
            }}
          >
            <TableCell
              sx={{
                fontWeight: 500,
                fontSize: { xs: '0.7rem', md: '0.75rem' },
                color: theme.palette.text.secondary,
                width: '40%',
                borderRight: `1px solid ${theme.palette.divider}`,
                paddingLeft: level === 0 ? { xs: 1, md: 1.5 } : { xs: 0.75, md: 1 },
                py: { xs: 0.5, md: 0.75 }
              }}
            >
              {fieldName}
            </TableCell>
            <TableCell
              sx={{
                fontSize: { xs: '0.7rem', md: '0.75rem' },
                wordBreak: 'break-word',
                color: theme.palette.text.primary,
                py: { xs: 0.5, md: 0.75 },
                fontWeight:
                  key.toLowerCase().includes('income') ||
                  key.toLowerCase().includes('value') ||
                  key.toLowerCase().includes('price') ||
                  key.toLowerCase().includes('amount')
                    ? 600
                    : 400
              }}
            >
              {displayValue}
            </TableCell>
          </TableRow>
        );
      }
    });

    return items;
  };

  // Get asset type icon
  const getAssetIcon = (type: string): React.ReactElement => {
    switch (type?.toLowerCase()) {
      case 'property':
        return <BusinessIcon />;
      case 'shares':
        return <TrendingUpIcon />;
      case 'bonds':
        return <AssessmentIcon />;
      default:
        return <BusinessIcon />;
    }
  };

  // Get asset color
  const getAssetColor = (type: string): string => {
    switch (type?.toLowerCase()) {
      case 'property':
        return theme.palette.mode === 'dark' ? '#4CAF50' : '#2E7D32';
      case 'shares':
        return theme.palette.mode === 'dark' ? '#42A5F5' : '#1565C0';
      case 'bonds':
        return theme.palette.mode === 'dark' ? '#FF9800' : '#E65100';
      default:
        return theme.palette.grey[700];
    }
  };

  // Render Asset Detail Modal
  const renderAssetDetailModal = () => (
    <Dialog
      open={assetDetailModal.open}
      onClose={closeAssetDetailModal}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          borderRadius: isMobile ? 0 : 2,
          boxShadow: theme.shadows[24]
        }
      }}
    >
      <DialogTitle
        sx={{
          p: { xs: 1.5, md: 2 },
          backgroundColor:
            theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 600,
                mb: 0.25,
                color: theme.palette.text.primary
              }}
            >
              Asset Details
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: { xs: '0.65rem', md: '0.7rem' }
              }}
            >
              Asset ID: {assetDetailModal.asset?.assetId}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
            <Chip
              label={assetDetailModal.asset?.type || 'Asset'}
              size="small"
              sx={{
                backgroundColor: assetDetailModal.asset
                  ? getAssetColor(assetDetailModal.asset.type)
                  : theme.palette.grey[700],
                color: 'white',
                fontWeight: 600,
                fontSize: { xs: '0.625rem', md: '0.65rem' },
                height: { xs: 20, md: 22 }
              }}
            />
            <IconButton onClick={closeAssetDetailModal} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: 0,
          overflow: 'auto',
          backgroundColor: theme.palette.background.default
        }}
      >
        <TableContainer component={Paper} elevation={0}>
          <Table size="small" stickyHeader>
            <TableBody>
              {assetDetailModal.asset && renderAssetKeyValuePairs(assetDetailModal.asset)}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions
        sx={{
          p: { xs: 1.5, md: 2 },
          backgroundColor:
            theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
          borderTop: `1px solid ${theme.palette.divider}`
        }}
      >
        <Button
          onClick={closeAssetDetailModal}
          variant="contained"
          size={isMobile ? 'small' : 'medium'}
          sx={{
            fontSize: { xs: '0.75rem', md: '0.8rem' },
            textTransform: 'none',
            px: { xs: 2, md: 3 },
            fontWeight: 500
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Render assets grid
  const renderAssetsGrid = () => (
    <>
      {loading && (!assets || assets.length === 0) ? (
        <Box display="flex" justifyContent="center" py={{ xs: 3, md: 4 }}>
          <CircularProgress size={isMobile ? 24 : 32} />
        </Box>
      ) : (
        <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
          {assets &&
            assets.map((asset: Asset) => (
              <Grid item xs={12} sm={6} md={6} lg={4} key={asset.assetId}>
                <Card
                  elevation={isMobile ? 1 : 2}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease-in-out',
                    borderRadius: { xs: 1.5, md: 2 },
                    border: `1px solid ${theme.palette.divider}`,
                    '&:hover': {
                      transform: isMobile ? 'none' : 'translateY(-4px)',
                      boxShadow: isMobile ? theme.shadows[2] : theme.shadows[8],
                      borderColor: theme.palette.primary.main
                    }
                  }}
                >
                  {/* Asset Header */}
                  <CardContent
                    sx={{
                      p: { xs: 1.25, md: 1.5 },
                      pb: { xs: 0.75, md: 1 },
                      '&:last-child': { pb: { xs: 0.75, md: 1 } }
                    }}
                  >
                    <Box display="flex" alignItems="flex-start" mb={{ xs: 0.75, md: 1 }}>
                      <Avatar
                        sx={{
                          bgcolor: getAssetColor(asset.type),
                          mr: { xs: 0.75, md: 1 },
                          width: { xs: 28, md: 36 },
                          height: { xs: 28, md: 36 },
                          mt: 0.25,
                          boxShadow: theme.shadows[2]
                        }}
                      >
                        {React.cloneElement(getAssetIcon(asset.type) as React.ReactElement<any>, {
                          sx: { fontSize: { xs: '1rem', md: '1.25rem' } }
                        })}
                      </Avatar>
                      <Box flex={1} minWidth={0}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: '0.8rem', md: '0.875rem' },
                            fontWeight: 600,
                            lineHeight: 1.3,
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            hyphens: 'auto',
                            mb: { xs: 0.35, md: 0.5 },
                            color: theme.palette.text.primary
                          }}
                        >
                          {asset.assetData.identifier}
                        </Typography>
                        <Chip
                          label={asset.assetData.subType?.replace('_', ' ').toUpperCase()}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{
                            fontSize: { xs: '0.5rem', md: '0.55rem' },
                            height: { xs: 18, md: 20 },
                            '& .MuiChip-label': {
                              px: { xs: 0.5, md: 0.75 },
                              py: 0
                            },
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: { xs: 0.75, md: 1 },
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontSize: { xs: '0.65rem', md: '0.7rem' },
                        lineHeight: 1.3
                      }}
                    >
                      {asset.assetData.description}
                    </Typography>

                    <Divider sx={{ my: { xs: 0.75, md: 1 } }} />

                    {/* Financial Details - Different fields for Income vs Growth */}
                    <Grid container spacing={{ xs: 0.75, md: 1 }}>
                      {/* Purchase Value - Common for both */}
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: { xs: '0.6rem', md: '0.65rem' },
                            lineHeight: 1.2,
                            display: 'block',
                            mb: 0.25
                          }}
                        >
                          Purchase Value
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: '0.7rem', md: '0.8rem' },
                            fontWeight: 700,
                            lineHeight: 1.2,
                            color: theme.palette.primary.main
                          }}
                        >
                          {formatCurrency(asset.assetData.purchaseValue)}
                        </Typography>
                      </Grid>

                      {/* Current Value - Common for both */}
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: { xs: '0.6rem', md: '0.65rem' },
                            lineHeight: 1.2,
                            display: 'block',
                            mb: 0.25
                          }}
                        >
                          Current Value
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: '0.7rem', md: '0.8rem' },
                            fontWeight: 700,
                            lineHeight: 1.2,
                            color:
                              parseFloat(
                                asset.currentValue || asset.assetData.currentValue || '0'
                              ) >= parseFloat(asset.assetData.purchaseValue || '0')
                                ? theme.palette.success.main
                                : theme.palette.error.main
                          }}
                        >
                          {formatCurrency(
                            asset.currentValue ||
                              asset.assetData.currentValue ||
                              asset.assetData.purchaseValue
                          )}
                        </Typography>
                      </Grid>

                      {currentUnitType === 'incomeUnits' ? (
                        // INCOME UNITS: Monthly Income + Monthly Expenses
                        <>
                          {/* Monthly Income */}
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: { xs: '0.6rem', md: '0.65rem' },
                                lineHeight: 1.2,
                                display: 'block',
                                mb: 0.25
                              }}
                            >
                              Monthly Income
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { xs: '0.7rem', md: '0.8rem' },
                                fontWeight: 700,
                                lineHeight: 1.2,
                                color: theme.palette.success.main
                              }}
                            >
                              +
                              {formatCurrency(
                                asset.assetData.monthlyIncome?.totalGrossIncome || '0'
                              )}
                            </Typography>
                          </Grid>

                          {/* Monthly Expenses */}
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: { xs: '0.6rem', md: '0.65rem' },
                                lineHeight: 1.2,
                                display: 'block',
                                mb: 0.25
                              }}
                            >
                              Monthly Expenses
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { xs: '0.7rem', md: '0.8rem' },
                                fontWeight: 700,
                                lineHeight: 1.2,
                                color: theme.palette.error.main
                              }}
                            >
                              -
                              {formatCurrency(
                                asset.assetData.monthlyExpenses?.totalExpenses || '0'
                              )}
                            </Typography>
                          </Grid>
                        </>
                      ) : (
                        // GROWTH UNITS: Monthly Income + Capital Appreciation
                        <>
                          {/* Monthly Income */}
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: { xs: '0.6rem', md: '0.65rem' },
                                lineHeight: 1.2,
                                display: 'block',
                                mb: 0.25
                              }}
                            >
                              Monthly Income
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { xs: '0.7rem', md: '0.8rem' },
                                fontWeight: 700,
                                lineHeight: 1.2,
                                color: theme.palette.success.main
                              }}
                            >
                              +
                              {formatCurrency(
                                asset.assetData.monthlyIncome?.totalGrossIncome || '0'
                              )}
                            </Typography>
                          </Grid>

                          {/* Capital Appreciation (Value Increase) */}
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: { xs: '0.6rem', md: '0.65rem' },
                                lineHeight: 1.2,
                                display: 'block',
                                mb: 0.25
                              }}
                            >
                              Value Increase
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { xs: '0.7rem', md: '0.8rem' },
                                fontWeight: 700,
                                lineHeight: 1.2,
                                color: (() => {
                                  const currentVal = parseFloat(
                                    asset.currentValue || asset.assetData.currentValue || '0'
                                  );
                                  const purchaseVal = parseFloat(
                                    asset.assetData.purchaseValue || '0'
                                  );
                                  const gain = currentVal - purchaseVal;
                                  return gain >= 0
                                    ? theme.palette.success.main
                                    : theme.palette.error.main;
                                })()
                              }}
                            >
                              {(() => {
                                const currentVal = parseFloat(
                                  asset.currentValue || asset.assetData.currentValue || '0'
                                );
                                const purchaseVal = parseFloat(
                                  asset.assetData.purchaseValue || '0'
                                );
                                const gain = currentVal - purchaseVal;
                                const percentage =
                                  purchaseVal > 0 ? ((gain / purchaseVal) * 100).toFixed(1) : '0.0';
                                return `${gain >= 0 ? '+' : ''}${percentage}% (${formatCurrency(
                                  gain
                                )})`;
                              })()}
                            </Typography>
                          </Grid>
                        </>
                      )}
                    </Grid>

                    {/* Additional Important Metrics */}
                    <Divider sx={{ my: { xs: 0.75, md: 1 } }} />

                    <Grid container spacing={{ xs: 0.75, md: 1 }}>
                      {/* Capital Gain/Loss */}
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: { xs: '0.6rem', md: '0.65rem' },
                            lineHeight: 1.2,
                            display: 'block',
                            mb: 0.25
                          }}
                        >
                          Capital Gain/Loss
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                            fontWeight: 700,
                            lineHeight: 1.2,
                            color: (() => {
                              const currentVal = parseFloat(
                                asset.currentValue || asset.assetData.currentValue || '0'
                              );
                              const purchaseVal = parseFloat(asset.assetData.purchaseValue || '0');
                              const gain = currentVal - purchaseVal;
                              return gain >= 0
                                ? theme.palette.success.main
                                : theme.palette.error.main;
                            })()
                          }}
                        >
                          {(() => {
                            const currentVal = parseFloat(
                              asset.currentValue || asset.assetData.currentValue || '0'
                            );
                            const purchaseVal = parseFloat(asset.assetData.purchaseValue || '0');
                            const gain = currentVal - purchaseVal;
                            const percentage =
                              purchaseVal > 0 ? ((gain / purchaseVal) * 100).toFixed(1) : '0.0';
                            return `${formatCurrency(gain)} (${
                              gain >= 0 ? '+' : ''
                            }${percentage}%)`;
                          })()}
                        </Typography>
                      </Grid>

                      {/* Liability Amount */}
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: { xs: '0.6rem', md: '0.65rem' },
                            lineHeight: 1.2,
                            display: 'block',
                            mb: 0.25
                          }}
                        >
                          Total Liabilities
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                            fontWeight: 700,
                            lineHeight: 1.2,
                            color:
                              asset.assetData.liabilities?.length > 0 &&
                              asset.assetData.liabilities[0]?.amount
                                ? theme.palette.warning.main
                                : theme.palette.text.secondary
                          }}
                        >
                          {(() => {
                            const totalLiabilities =
                              asset.assetData.liabilities?.reduce((total, liability) => {
                                return total + parseFloat(liability.amount || '0');
                              }, 0) || 0;
                            return totalLiabilities > 0 ? formatCurrency(totalLiabilities) : 'None';
                          })()}
                        </Typography>
                      </Grid>

                      {asset?.isSold && (
                        <Chip
                          label={`Sold: ${formatCurrency(parseFloat(asset?.sellValue || '') || 0)}`}
                          color="error"
                          size="small"
                          sx={{ ml: 1, mt: 1, fontWeight: 'bold' }}
                        />
                      )}
                    </Grid>

                    <Divider sx={{ my: { xs: 0.75, md: 1 } }} />

                    {/* Asset Info */}
                    <Box display="flex" alignItems="center" mb={{ xs: 0.25, md: 0.35 }}>
                      <PersonIcon
                        sx={{
                          mr: 0.5,
                          fontSize: { xs: 12, md: 14 },
                          color: theme.palette.text.secondary
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: { xs: '0.6rem', md: '0.65rem' },
                          lineHeight: 1.2,
                          wordBreak: 'break-word'
                        }}
                      >
                        Manager: {asset.fundManager}
                      </Typography>
                    </Box>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.6rem', md: '0.65rem' },
                        lineHeight: 1.2
                      }}
                    >
                      Purchased: {formatDate(asset.purchaseDate)}
                    </Typography>
                  </CardContent>

                  {/* ✅ ACTION BUTTONS - Including NEW Sell Button */}
                  <Box
                    sx={{
                      p: { xs: 1, md: 1.25 },
                      pt: 0,
                      mt: 'auto',
                      display: 'flex',
                      gap: { xs: 0.75, md: 1 }
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ViewIcon sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }} />}
                      onClick={() => openAssetDetailModal(asset)}
                      sx={{
                        flex: 1,
                        fontSize: { xs: '0.65rem', md: '0.7rem' },
                        py: { xs: 0.5, md: 0.625 },
                        minHeight: { xs: 28, md: 32 },
                        textTransform: 'none',
                        fontWeight: 600,
                        borderWidth: 1.5,
                        '&:hover': {
                          borderWidth: 1.5,
                          backgroundColor: theme.palette.action.hover
                        }
                      }}
                    >
                      View
                    </Button>{' '}
                    {!asset?.isSold && (
                      <>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={
                            <AssessmentIcon sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }} />
                          }
                          onClick={() => openRevaluationModal(asset)}
                          sx={{
                            flex: 1,
                            bgcolor: theme.palette.secondary.main,
                            '&:hover': {
                              bgcolor: theme.palette.secondary.dark,
                              transform: 'scale(1.02)'
                            },
                            fontSize: { xs: '0.65rem', md: '0.7rem' },
                            py: { xs: 0.5, md: 0.625 },
                            minHeight: { xs: 28, md: 32 },
                            textTransform: 'none',
                            fontWeight: 600,
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          Revalue
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<SellIcon sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }} />}
                          onClick={() => handleSellAsset(asset)}
                          sx={{
                            flex: 1,
                            bgcolor: theme.palette.warning.main,
                            color: theme.palette.warning.contrastText,
                            '&:hover': {
                              bgcolor: theme.palette.warning.dark,
                              transform: 'scale(1.02)'
                            },
                            fontSize: { xs: '0.65rem', md: '0.7rem' },
                            py: { xs: 0.5, md: 0.625 },
                            minHeight: { xs: 28, md: 32 },
                            textTransform: 'none',
                            fontWeight: 600,
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          Sell
                        </Button>
                      </>
                    )}
                  </Box>
                </Card>
              </Grid>
            ))}
        </Grid>
      )}

      {/* Load More Button */}
      {pagination?.hasMore && (
        <Box display="flex" justifyContent="center" mt={{ xs: 1.5, md: 2.5 }}>
          <Button
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            onClick={handleLoadMore}
            disabled={loading}
            startIcon={loading && <CircularProgress size={isMobile ? 14 : 18} />}
            sx={{
              fontSize: { xs: '0.7rem', md: '0.8rem' },
              py: { xs: 0.75, md: 1 },
              px: { xs: 2.5, md: 3 },
              textTransform: 'none',
              fontWeight: 600,
              borderWidth: 1.5,
              '&:hover': {
                borderWidth: 1.5,
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[4]
              }
            }}
          >
            {loading ? 'Loading...' : 'Load More Assets'}
          </Button>
        </Box>
      )}

      {/* No Assets Message */}
      {!loading && (!assets || assets.length === 0) && (
        <Box textAlign="center" py={{ xs: 3, md: 4 }}>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            gutterBottom
            sx={{ fontSize: { xs: '0.875rem', md: '0.95rem' }, fontWeight: 600 }}
          >
            No {tabValue === 0 ? 'Income' : 'Growth'} Assets Found
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
          >
            There are currently no assets in this category.
          </Typography>
        </Box>
      )}
    </>
  );

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Container
        maxWidth={false}
        sx={{
          py: { xs: 1, sm: 1.5, md: 2 },
          px: { xs: 0.75, sm: 1, md: 1.5 }
        }}
      >
        {/* Header */}
        <Box sx={{ mb: { xs: 1, md: 1.5 } }}>
          <Typography
            variant="h6"
            component="h1"
            sx={{
              fontWeight: 600,
              color: theme.palette.primary.main,
              textAlign: 'center',
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              lineHeight: 1.3,
              mb: 0
            }}
          >
            Asset Portfolio
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: { xs: 1, md: 1.5 },
              fontSize: { xs: '0.7rem', md: '0.75rem' },
              borderRadius: { xs: 1, md: 1.5 },
              py: { xs: 0.5, md: 0.75 }
            }}
          >
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ mb: { xs: 1, md: 1.5 } }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="asset portfolio tabs"
            variant="fullWidth"
            sx={{
              borderBottom: 1.5,
              borderColor: 'divider',
              minHeight: { xs: 36, md: 44 },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: { xs: '0.7rem', md: '0.8rem' },
                fontWeight: 600,
                py: { xs: 1, md: 1.25 },
                minHeight: { xs: 36, md: 44 },
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                },
                '&.Mui-selected': {
                  color: theme.palette.primary.main
                }
              },
              '& .MuiTabs-indicator': {
                height: 2.5,
                borderRadius: '2.5px 2.5px 0 0'
              }
            }}
          >
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={{ xs: 0.5, md: 0.75 }}>
                  <MoneyIcon sx={{ fontSize: { xs: '1rem', md: '1.125rem' } }} />
                  <span>Income Assets</span>
                </Box>
              }
              {...a11yProps(0)}
            />
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={{ xs: 0.5, md: 0.75 }}>
                  <TrendingUpIcon sx={{ fontSize: { xs: '1rem', md: '1.125rem' } }} />
                  <span>Growth Assets</span>
                </Box>
              }
              {...a11yProps(1)}
            />
          </Tabs>

          {/* Tab Panels */}
          <TabPanel value={tabValue} index={0}>
            {renderAssetsGrid()}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {renderAssetsGrid()}
          </TabPanel>
        </Box>

        {/* Asset Detail Modal */}
        {renderAssetDetailModal()}

        {/* Revaluation Modal */}
        <RevaluationModal
          open={revaluationModal.open}
          onClose={closeRevaluationModal}
          asset={revaluationModal.asset}
          unitType={currentUnitType}
          onSuccess={handleRevaluationSuccess}
        />
      </Container>
    </Box>
  );
};

export default AssetList;
